// SchemePay Reconciliation Dashboard - Enterprise Sample Data Generator
import * as XLSX from 'xlsx';
import { RazorpayColumnMapping, ErpColumnMapping } from '../types/reconciliation';

export const DEFAULT_RAZORPAY_MAPPING: RazorpayColumnMapping = {
  paymentId: 'Payment ID',
  amount: 'Amount',
  customerName: 'Customer Name',
  paymentDate: 'Payment Date',
  status: 'Status',
  refundAmount: 'Refund Amount',
  refundDate: 'Refund Date',
};

export const DEFAULT_ERP_MAPPING: ErpColumnMapping = {
  paymentId: 'Payment ID',
  billNumber: 'Bill Number',
  billAmount: 'Bill Amount',
  customerName: 'Customer Name',
  postingDate: 'Posting Date',
};

export interface EnterpriseSampleDataset {
  razorpayRows: Record<string, any>[];
  erpRows: Record<string, any>[];
  razorpayMapping: RazorpayColumnMapping;
  erpMapping: ErpColumnMapping;
}

export function generateEnterpriseSampleData(): EnterpriseSampleDataset {
  // 1. Razorpay Report Records
  const razorpayRows: Record<string, any>[] = [
    // Case 1: Multiple ERP Bills exact match (User prompt's exact example)
    {
      'Payment ID': 'RP001',
      'Amount': 10000,
      'Customer Name': 'Aditi Gold Scheme - Rajesh Sharma',
      'Payment Date': '2026-09-01 10:15:22',
      'Status': 'captured',
      'Refund Amount': 0,
      'Refund Date': '',
    },
    // Case 2: Multi-bill exact match (2 bills)
    {
      'Payment ID': 'RP002',
      'Amount': 25000,
      'Customer Name': 'Dhanvantari Jewel Scheme - Suman Rao',
      'Payment Date': '2026-09-01 11:30:10',
      'Status': 'captured',
      'Refund Amount': 0,
      'Refund Date': '',
    },
    // Case 3: Single bill exact match
    {
      'Payment ID': 'RP003',
      'Amount': 5000,
      'Customer Name': 'Monthly Silver Scheme - Amit Verma',
      'Payment Date': '2026-09-01 12:45:00',
      'Status': 'captured',
      'Refund Amount': 0,
      'Refund Date': '',
    },
    // Case 4: Partial Match (Razorpay > ERP Total: Under-billed in ERP)
    {
      'Payment ID': 'RP004',
      'Amount': 15000,
      'Customer Name': 'Samruddhi Scheme - Meera Nair',
      'Payment Date': '2026-09-02 09:20:44',
      'Status': 'captured',
      'Refund Amount': 0,
      'Refund Date': '',
    },
    // Case 5: Over Billed (ERP Total > Razorpay Amount)
    {
      'Payment ID': 'RP005',
      'Amount': 18000,
      'Customer Name': 'Swarna Varsha - Vikram Sethi',
      'Payment Date': '2026-09-02 14:10:15',
      'Status': 'captured',
      'Refund Amount': 0,
      'Refund Date': '',
    },
    // Case 6: No Bill Reference (In Razorpay, zero ERP bills)
    {
      'Payment ID': 'RP006',
      'Amount': 12500,
      'Customer Name': 'Kalyan Diamond Scheme - Pooja Joshi',
      'Payment Date': '2026-09-03 16:05:30',
      'Status': 'captured',
      'Refund Amount': 0,
      'Refund Date': '',
    },
    // Case 7: Refunded Transaction
    {
      'Payment ID': 'RP007',
      'Amount': 8000,
      'Customer Name': 'Suvarna Nidhi - Rakesh Patel',
      'Payment Date': '2026-09-03 17:40:00',
      'Status': 'refunded',
      'Refund Amount': 8000,
      'Refund Date': '2026-09-04 11:20:10',
    },
    // Case 8: Multi-bill exact match (4 split bills)
    {
      'Payment ID': 'RP008',
      'Amount': 40000,
      'Customer Name': 'Royal Mahalakshmi Scheme - Sunita Agarwal',
      'Payment Date': '2026-09-04 10:00:12',
      'Status': 'captured',
      'Refund Amount': 0,
      'Refund Date': '',
    },
    // Case 9: Partial Match (Under-billed)
    {
      'Payment ID': 'RP009',
      'Amount': 30000,
      'Customer Name': 'Akshaya Tritiya Saver - Deepak Chawla',
      'Payment Date': '2026-09-04 15:22:18',
      'Status': 'captured',
      'Refund Amount': 0,
      'Refund Date': '',
    },
    // Case 10: Exact Match (2 bills)
    {
      'Payment ID': 'RP010',
      'Amount': 22000,
      'Customer Name': 'Platinum Bullion Scheme - Farhan Khan',
      'Payment Date': '2026-09-05 13:12:00',
      'Status': 'captured',
      'Refund Amount': 0,
      'Refund Date': '',
    },
    // Case 11: Exact Match (1 bill)
    {
      'Payment ID': 'RP011',
      'Amount': 6500,
      'Customer Name': 'Smart Gold Accumulator - Bhavna Shah',
      'Payment Date': '2026-09-05 16:45:11',
      'Status': 'captured',
      'Refund Amount': 0,
      'Refund Date': '',
    },
    // Case 12: Refunded Transaction
    {
      'Payment ID': 'RP012',
      'Amount': 11000,
      'Customer Name': 'Vardhman Scheme - Manoj Tiwari',
      'Payment Date': '2026-09-06 11:05:09',
      'Status': 'refunded',
      'Refund Amount': 11000,
      'Refund Date': '2026-09-07 09:15:00',
    },
    // Case 13: No Bill Reference
    {
      'Payment ID': 'RP013',
      'Amount': 14500,
      'Customer Name': 'Navratna Jewel Deposit - Kavita Kulkarni',
      'Payment Date': '2026-09-06 14:50:33',
      'Status': 'captured',
      'Refund Amount': 0,
      'Refund Date': '',
    },
    // Case 14: Over Billed
    {
      'Payment ID': 'RP014',
      'Amount': 16000,
      'Customer Name': 'Gold Mohur Scheme - Sandeep Singhania',
      'Payment Date': '2026-09-07 10:30:45',
      'Status': 'captured',
      'Refund Amount': 0,
      'Refund Date': '',
    },
    // Case 15: Multi-bill exact match (3 bills)
    {
      'Payment ID': 'RP015',
      'Amount': 35000,
      'Customer Name': 'Heritage Jewelry Club - Ananya Deshmukh',
      'Payment Date': '2026-09-07 18:20:00',
      'Status': 'captured',
      'Refund Amount': 0,
      'Refund Date': '',
    },
  ];

  // 2. ERP Billing Report Records (Notice: Multiple bills for same Payment ID)
  const erpRows: Record<string, any>[] = [
    // RP001 -> Bill A (4000) + Bill B (3000) + Bill C (3000) = 10,000 (Exact Match)
    {
      'Payment ID': 'RP001',
      'Bill Number': 'BILL-2026-001A',
      'Bill Amount': 4000,
      'Customer Name': 'Rajesh Sharma',
      'Posting Date': '2026-09-01',
    },
    {
      'Payment ID': 'RP001',
      'Bill Number': 'BILL-2026-001B',
      'Bill Amount': 3000,
      'Customer Name': 'Rajesh Sharma',
      'Posting Date': '2026-09-01',
    },
    {
      'Payment ID': 'RP001',
      'Bill Number': 'BILL-2026-001C',
      'Bill Amount': 3000,
      'Customer Name': 'Rajesh Sharma',
      'Posting Date': '2026-09-01',
    },

    // RP002 -> Bill 002A (15,000) + Bill 002B (10,000) = 25,000 (Exact Match)
    {
      'Payment ID': 'RP002',
      'Bill Number': 'BILL-2026-002A',
      'Bill Amount': 15000,
      'Customer Name': 'Suman Rao',
      'Posting Date': '2026-09-01',
    },
    {
      'Payment ID': 'RP002',
      'Bill Number': 'BILL-2026-002B',
      'Bill Amount': 10000,
      'Customer Name': 'Suman Rao',
      'Posting Date': '2026-09-01',
    },

    // RP003 -> Single Bill (5000) = 5000 (Exact Match)
    {
      'Payment ID': 'RP003',
      'Bill Number': 'BILL-2026-003',
      'Bill Amount': 5000,
      'Customer Name': 'Amit Verma',
      'Posting Date': '2026-09-01',
    },

    // RP004 -> Bill A (8,000) + Bill B (4,000) = 12,000 vs RP 15,000 (Partial Match, diff = +3,000)
    {
      'Payment ID': 'RP004',
      'Bill Number': 'BILL-2026-004A',
      'Bill Amount': 8000,
      'Customer Name': 'Meera Nair',
      'Posting Date': '2026-09-02',
    },
    {
      'Payment ID': 'RP004',
      'Bill Number': 'BILL-2026-004B',
      'Bill Amount': 4000,
      'Customer Name': 'Meera Nair',
      'Posting Date': '2026-09-02',
    },

    // RP005 -> Bill A (12,000) + Bill B (9,000) = 21,000 vs RP 18,000 (Over Billed, diff = -3,000)
    {
      'Payment ID': 'RP005',
      'Bill Number': 'BILL-2026-005A',
      'Bill Amount': 12000,
      'Customer Name': 'Vikram Sethi',
      'Posting Date': '2026-09-02',
    },
    {
      'Payment ID': 'RP005',
      'Bill Number': 'BILL-2026-005B',
      'Bill Amount': 9000,
      'Customer Name': 'Vikram Sethi',
      'Posting Date': '2026-09-02',
    },

    // (Note: RP006 has NO bills in ERP -> No Bill Reference)

    // RP007 -> Bill A (8,000) (Refunded in Razorpay)
    {
      'Payment ID': 'RP007',
      'Bill Number': 'BILL-2026-007',
      'Bill Amount': 8000,
      'Customer Name': 'Rakesh Patel',
      'Posting Date': '2026-09-03',
    },

    // RP008 -> 4 Split Bills: 10,000 + 10,000 + 10,000 + 10,000 = 40,000 (Exact Match)
    {
      'Payment ID': 'RP008',
      'Bill Number': 'BILL-2026-008A',
      'Bill Amount': 10000,
      'Customer Name': 'Sunita Agarwal',
      'Posting Date': '2026-09-04',
    },
    {
      'Payment ID': 'RP008',
      'Bill Number': 'BILL-2026-008B',
      'Bill Amount': 10000,
      'Customer Name': 'Sunita Agarwal',
      'Posting Date': '2026-09-04',
    },
    {
      'Payment ID': 'RP008',
      'Bill Number': 'BILL-2026-008C',
      'Bill Amount': 10000,
      'Customer Name': 'Sunita Agarwal',
      'Posting Date': '2026-09-04',
    },
    {
      'Payment ID': 'RP008',
      'Bill Number': 'BILL-2026-008D',
      'Bill Amount': 10000,
      'Customer Name': 'Sunita Agarwal',
      'Posting Date': '2026-09-04',
    },

    // RP009 -> Bill A (18,000) + Bill B (7,000) = 25,000 vs RP 30,000 (Partial Match, diff = +5,000)
    {
      'Payment ID': 'RP009',
      'Bill Number': 'BILL-2026-009A',
      'Bill Amount': 18000,
      'Customer Name': 'Deepak Chawla',
      'Posting Date': '2026-09-04',
    },
    {
      'Payment ID': 'RP009',
      'Bill Number': 'BILL-2026-009B',
      'Bill Amount': 7000,
      'Customer Name': 'Deepak Chawla',
      'Posting Date': '2026-09-04',
    },

    // RP010 -> Bill A (12,000) + Bill B (10,000) = 22,000 (Exact Match)
    {
      'Payment ID': 'RP010',
      'Bill Number': 'BILL-2026-010A',
      'Bill Amount': 12000,
      'Customer Name': 'Farhan Khan',
      'Posting Date': '2026-09-05',
    },
    {
      'Payment ID': 'RP010',
      'Bill Number': 'BILL-2026-010B',
      'Bill Amount': 10000,
      'Customer Name': 'Farhan Khan',
      'Posting Date': '2026-09-05',
    },

    // RP011 -> Single Bill (6,500) = 6,500 (Exact Match)
    {
      'Payment ID': 'RP011',
      'Bill Number': 'BILL-2026-011',
      'Bill Amount': 6500,
      'Customer Name': 'Bhavna Shah',
      'Posting Date': '2026-09-05',
    },

    // RP014 -> Bill A (10,000) + Bill B (8,500) = 18,500 vs RP 16,000 (Over Billed, diff = -2,500)
    {
      'Payment ID': 'RP014',
      'Bill Number': 'BILL-2026-014A',
      'Bill Amount': 10000,
      'Customer Name': 'Sandeep Singhania',
      'Posting Date': '2026-09-07',
    },
    {
      'Payment ID': 'RP014',
      'Bill Number': 'BILL-2026-014B',
      'Bill Amount': 8500,
      'Customer Name': 'Sandeep Singhania',
      'Posting Date': '2026-09-07',
    },

    // RP015 -> Bill A (15,000) + Bill B (10,000) + Bill C (10,000) = 35,000 (Exact Match)
    {
      'Payment ID': 'RP015',
      'Bill Number': 'BILL-2026-015A',
      'Bill Amount': 15000,
      'Customer Name': 'Ananya Deshmukh',
      'Posting Date': '2026-09-07',
    },
    {
      'Payment ID': 'RP015',
      'Bill Number': 'BILL-2026-015B',
      'Bill Amount': 10000,
      'Customer Name': 'Ananya Deshmukh',
      'Posting Date': '2026-09-07',
    },
    {
      'Payment ID': 'RP015',
      'Bill Number': 'BILL-2026-015C',
      'Bill Amount': 10000,
      'Customer Name': 'Ananya Deshmukh',
      'Posting Date': '2026-09-07',
    },

    // ERP ONLY RECORDS (Rule 5: In ERP but not in Razorpay)
    {
      'Payment ID': 'RP_ERP_901',
      'Bill Number': 'BILL-MANUAL-901A',
      'Bill Amount': 14000,
      'Customer Name': 'Nitin Gadkari (Direct Bank/Counter)',
      'Posting Date': '2026-09-06',
    },
    {
      'Payment ID': 'RP_ERP_901',
      'Bill Number': 'BILL-MANUAL-901B',
      'Bill Amount': 6000,
      'Customer Name': 'Nitin Gadkari (Direct Bank/Counter)',
      'Posting Date': '2026-09-06',
    },
    {
      'Payment ID': 'RP_ERP_902',
      'Bill Number': 'BILL-MANUAL-902',
      'Bill Amount': 9500,
      'Customer Name': 'Geeta Mittal (Offline RTGS)',
      'Posting Date': '2026-09-07',
    },
  ];

  return {
    razorpayRows,
    erpRows,
    razorpayMapping: DEFAULT_RAZORPAY_MAPPING,
    erpMapping: DEFAULT_ERP_MAPPING,
  };
}

/**
 * Downloads sample Razorpay or ERP Excel file for users to inspect or test manual uploads
 */
export function downloadSampleExcel(type: 'razorpay' | 'erp'): void {
  const sample = generateEnterpriseSampleData();
  const data = type === 'razorpay' ? sample.razorpayRows : sample.erpRows;
  const fileName = type === 'razorpay' ? 'Sample_Razorpay_Payment_Report.xlsx' : 'Sample_ERP_Billing_Report.xlsx';

  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, type === 'razorpay' ? 'Razorpay Payments' : 'ERP Bills');
  XLSX.writeFile(wb, fileName);
}
